/**
 * useGeolocation Hook
 * 
 * Cross-platform geolocation for:
 * - Mobile App: Uses Capacitor Geolocation API (GPS)
 * - Installed/Web PWA: Uses browser Geolocation API when available
 * - Desktop App: Uses desktop bridge (WiFi/Beacon proxy)
 * 
 * For mobile apps and installed PWAs, this hook provides:
 * - Real-time GPS coordinates
 * - Geofence validation
 * - Distance calculations
 * - Background location tracking
 * 
 * @author TotalAccess Development Team
 * @date November 23, 2025
 */

'use client';

import { useState, useEffect, useCallback, useRef } from 'react';
import { Capacitor } from '@capacitor/core';
import { Geolocation } from '@/lib/channelGeolocation';
import type { Position, PermissionStatus } from '@capacitor/geolocation';
import { App } from '@capacitor/app';
import { isBiometricLockActive, waitForBiometricUnlock } from '@/lib/biometricLockState';
import { openNativeAppSettings } from '@/lib/cameraAccess';
import { debugLog, debugError } from '@/lib/debugLogger';

type ErrorWithMessage = { message?: string };

function getErrorMessage(error: unknown, fallback: string): string {
  if (error instanceof Error) {
    return error.message || fallback;
  }

  if (typeof error === 'object' && error && 'message' in error) {
    const maybeMessage = (error as ErrorWithMessage).message;
    return typeof maybeMessage === 'string' && maybeMessage.trim().length > 0
      ? maybeMessage
      : fallback;
  }

  return fallback;
}

const SHARED_GEOLOCATION_FRESH_MS = 2 * 60 * 1000;
let sharedGeolocationSnapshot: GeolocationCoords | null = null;
let sharedGeolocationWarmPromise: Promise<GeolocationCoords | null> | null = null;
let nativeSettingsModulePromise: Promise<typeof import('capacitor-native-settings')> | null = null;
let openLocationSettingsPromise: Promise<void> | null = null;

// ============================================================================
// SHARED GEOLOCATION STATUS STORE
//
// useGeolocation is mounted concurrently in many places (SmartClock,
// SmartTaskShell, BeginActionModal, useLocationValidation, ...). Without a
// shared store every instance:
//   - runs its own 5s setInterval permission poll
//   - starts its own native Geolocation.watchPosition for service-status
//   - starts at permissionGranted=false until ITS first checkPermissions resolves
// On Capacitor that saturates the native bridge, slows every individual call,
// and leaves some consumers reporting "location disabled" for minutes even
// when the OS has location enabled. We solve this by sharing the booleans
// across all consumers and running a SINGLE permission poll + a SINGLE
// native service-status watcher for the whole app.
// ============================================================================

interface GeolocationStatusSnapshot {
  permissionGranted: boolean;
  permissionDenied: boolean;
  locationServicesDisabled: boolean;
}

let geolocationStatusState: GeolocationStatusSnapshot = {
  permissionGranted: false,
  permissionDenied: false,
  locationServicesDisabled: false,
};

const geolocationStatusListeners = new Set<(s: GeolocationStatusSnapshot) => void>();
const geolocationPermissionChangeCallbacks = new Set<(granted: boolean) => void>();
const geolocationServicesChangeCallbacks = new Set<(enabled: boolean) => void>();

let lastBroadcastPermissionGranted: boolean | null = null;
let lastBroadcastServicesEnabled: boolean | null = null;

function getGeolocationStatusSnapshot(): GeolocationStatusSnapshot {
  return geolocationStatusState;
}

function setSharedGeolocationStatus(patch: Partial<GeolocationStatusSnapshot>): void {
  const next: GeolocationStatusSnapshot = {
    permissionGranted: patch.permissionGranted ?? geolocationStatusState.permissionGranted,
    permissionDenied: patch.permissionDenied ?? geolocationStatusState.permissionDenied,
    locationServicesDisabled: patch.locationServicesDisabled ?? geolocationStatusState.locationServicesDisabled,
  };

  if (
    next.permissionGranted === geolocationStatusState.permissionGranted
    && next.permissionDenied === geolocationStatusState.permissionDenied
    && next.locationServicesDisabled === geolocationStatusState.locationServicesDisabled
  ) {
    return;
  }

  const grantedChanged = lastBroadcastPermissionGranted !== null && lastBroadcastPermissionGranted !== next.permissionGranted;
  const servicesEnabled = !next.locationServicesDisabled;
  const servicesChanged = lastBroadcastServicesEnabled !== null && lastBroadcastServicesEnabled !== servicesEnabled;

  geolocationStatusState = next;
  lastBroadcastPermissionGranted = next.permissionGranted;
  lastBroadcastServicesEnabled = servicesEnabled;

  geolocationStatusListeners.forEach((listener) => {
    try { listener(next); } catch (err) { console.warn('[Geolocation] status listener failed:', err); }
  });

  if (grantedChanged) {
    geolocationPermissionChangeCallbacks.forEach((cb) => {
      try { cb(next.permissionGranted); } catch (err) { console.warn('[Geolocation] permission-change callback failed:', err); }
    });
  }
  if (servicesChanged) {
    geolocationServicesChangeCallbacks.forEach((cb) => {
      try { cb(servicesEnabled); } catch (err) { console.warn('[Geolocation] services-change callback failed:', err); }
    });
  }
}

function subscribeToGeolocationStatus(listener: (s: GeolocationStatusSnapshot) => void): () => void {
  geolocationStatusListeners.add(listener);
  return () => { geolocationStatusListeners.delete(listener); };
}

// ---------- Singleton permission poll (one shared 5s interval) ----------

let sharedPermissionMonitorRefcount = 0;
let sharedPermissionMonitorTimer: ReturnType<typeof setInterval> | null = null;
let sharedPermissionCheckInFlight: Promise<boolean> | null = null;

/**
 * Fold a permission query result into the shared status store.
 *
 * Two guards keep transient OS answers from producing false "permission
 * missing" states:
 *  - Permission queries cannot see the OS-level location-services toggle,
 *    so this never touches `locationServicesDisabled` (a successful
 *    position fix is the only signal that clears it).
 *  - A fresh position fix is more authoritative than a 'prompt' result —
 *    the OS cannot have handed us coordinates without holding permission.
 *    'prompt' can be reported transiently while another consumer's
 *    request dialog is in flight, so only an explicit 'denied' (or a
 *    'prompt-with-rationale' notHeld hint) downgrades a held grant.
 */
function applySharedPermissionCheckResult(result: {
  granted: boolean;
  denied: boolean;
  /** OS reports the permission is not currently held (e.g. 'denied' or
   *  'prompt-with-rationale') — bypasses the fresh-fix guard so a real
   *  revocation still downgrades a held grant. */
  notHeld?: boolean;
}): { granted: boolean; denied: boolean } {
  const hasFreshFix = getSharedGeolocationSnapshot() !== null;
  const granted = result.denied || result.notHeld ? false : (result.granted || hasFreshFix);
  setSharedGeolocationStatus({
    permissionGranted: granted,
    permissionDenied: result.denied,
  });
  return { granted, denied: result.denied };
}

// ---------- Singleton native requestPermissions dedupe ----------
// Multiple mounted useGeolocation instances can auto-request at the same
// time on cold start. Android only honours ONE in-flight permission
// request — concurrent requestPermissions calls can resolve with a
// spurious 'prompt'/'denied' result and clobber the shared store. Dedupe
// the raw OS call so every consumer shares a single OS prompt.
let sharedNativePermissionRequestInFlight: Promise<PermissionStatus> | null = null;

function requestNativePermissionDeduped(): Promise<PermissionStatus> {
  if (!sharedNativePermissionRequestInFlight) {
    sharedNativePermissionRequestInFlight = Geolocation.requestPermissions()
      .finally(() => {
        sharedNativePermissionRequestInFlight = null;
      });
  }
  return sharedNativePermissionRequestInFlight;
}

async function performSharedPermissionCheck(): Promise<boolean> {
  if (sharedPermissionCheckInFlight) {
    return sharedPermissionCheckInFlight;
  }

  const isMobile = typeof Capacitor !== 'undefined' && Capacitor.isNativePlatform();
  const isNativeSupported = isMobile && Capacitor.isPluginAvailable('Geolocation');

  sharedPermissionCheckInFlight = (async () => {
    if (!isMobile) {
      try {
        if (typeof navigator !== 'undefined' && navigator.permissions?.query) {
          const permission = await navigator.permissions.query({ name: 'geolocation' as PermissionName });
          const { granted } = applySharedPermissionCheckResult({
            granted: permission.state === 'granted',
            denied: permission.state === 'denied',
          });
          // Web cannot distinguish OS-level location-off from transient
          // POSITION_UNAVAILABLE failures — keep clearing the flag here so
          // a transient error cannot leave it stuck on.
          setSharedGeolocationStatus({ locationServicesDisabled: false });
          return granted;
        }
      } catch {
        // ignore — Permissions API unavailable
      }
      return geolocationStatusState.permissionGranted;
    }

    if (!isNativeSupported) {
      return geolocationStatusState.permissionGranted;
    }

    try {
      const permission: PermissionStatus = await Geolocation.checkPermissions();
      const { granted } = applySharedPermissionCheckResult({
        granted: permission.location === 'granted' || permission.coarseLocation === 'granted',
        denied: permission.location === 'denied' && permission.coarseLocation !== 'granted',
        notHeld: permission.location === 'denied' || permission.location === 'prompt-with-rationale',
      });
      return granted;
    } catch (err: unknown) {
      const errorMsg = getErrorMessage(err, '');
      if (isLocationServicesErrorMessage(errorMsg)) {
        setSharedGeolocationStatus({ locationServicesDisabled: true });
        return false;
      }
      // Unexpected — leave shared status alone, return current
      return geolocationStatusState.permissionGranted;
    }
  })();

  try {
    return await sharedPermissionCheckInFlight;
  } finally {
    sharedPermissionCheckInFlight = null;
  }
}

function registerSharedPermissionMonitor(): () => void {
  sharedPermissionMonitorRefcount += 1;
  if (sharedPermissionMonitorRefcount === 1) {
    void performSharedPermissionCheck();
    sharedPermissionMonitorTimer = setInterval(() => {
      void performSharedPermissionCheck();
    }, 3000);
    debugLog('[Geolocation]', 'Shared permission monitor started (3s interval)');
  }

  let released = false;
  return () => {
    if (released) return;
    released = true;
    sharedPermissionMonitorRefcount = Math.max(0, sharedPermissionMonitorRefcount - 1);
    if (sharedPermissionMonitorRefcount === 0 && sharedPermissionMonitorTimer) {
      clearInterval(sharedPermissionMonitorTimer);
      sharedPermissionMonitorTimer = null;
      debugLog('[Geolocation]', 'Shared permission monitor stopped');
    }
  };
}

// ---------- Singleton native service-status watcher (one shared native watch) ----------

let sharedServiceStatusRefcount = 0;
let sharedServiceStatusWatchId: string | null = null;
let sharedServiceStatusStarting = false;

function registerSharedNativeServiceStatusWatcher(): () => void {
  const isMobile = typeof Capacitor !== 'undefined' && Capacitor.isNativePlatform();
  const isNativeSupported = isMobile && Capacitor.isPluginAvailable('Geolocation');

  if (!isNativeSupported) {
    return () => { };
  }

  sharedServiceStatusRefcount += 1;

  if (sharedServiceStatusRefcount === 1 && !sharedServiceStatusWatchId && !sharedServiceStatusStarting) {
    sharedServiceStatusStarting = true;
    void (async () => {
      try {
        const id = await Geolocation.watchPosition(
          {
            enableHighAccuracy: false,
            timeout: 10000,
            maximumAge: 30000,
          },
          (position, error) => {
            if (error) {
              if (isNativeLocationWatchError(error)) {
                debugLog('[Geolocation]', 'Shared native watcher detected location disabled');
                // Services being off is NOT a permission revocation — the
                // OS still holds the grant. Flag only the services state so
                // consumers don't falsely report "permission required".
                setSharedGeolocationStatus({ locationServicesDisabled: true });
              } else if (isNativePermissionWatchError(error)) {
                debugLog('[Geolocation]', 'Shared native watcher detected permission loss');
                setSharedGeolocationStatus({ permissionGranted: false, permissionDenied: true });
              }
              return;
            }
            if (!position) return;

            const coords = mapNativeGeoPositionToCoords(position);
            updateSharedGeolocationSnapshot(coords);
            setSharedGeolocationStatus({
              permissionGranted: true,
              permissionDenied: false,
              locationServicesDisabled: false,
            });
          },
        );
        sharedServiceStatusWatchId = id;
        debugLog('[Geolocation]', 'Shared native service status watcher started:', id);
      } catch (error: unknown) {
        console.warn('[Geolocation] Failed to start shared service status watcher:', getErrorMessage(error, 'Unknown watcher error'));
      } finally {
        sharedServiceStatusStarting = false;
      }
    })();
  }

  let released = false;
  return () => {
    if (released) return;
    released = true;
    sharedServiceStatusRefcount = Math.max(0, sharedServiceStatusRefcount - 1);
    if (sharedServiceStatusRefcount === 0 && sharedServiceStatusWatchId) {
      const id = sharedServiceStatusWatchId;
      sharedServiceStatusWatchId = null;
      void Geolocation.clearWatch({ id }).catch((clearError: unknown) => {
        const msg = getErrorMessage(clearError, String(clearError));
        if (!msg.includes('WatchId')) {
          console.warn('[Geolocation] Failed to stop shared service status watcher:', msg);
        }
      });
      debugLog('[Geolocation]', 'Shared native service status watcher stopped');
    }
  };
}

// ==================== TYPES ====================

export interface GeolocationCoords {
  latitude: number;
  longitude: number;
  accuracy: number;
  timestamp: number;
  altitude?: number;
  altitudeAccuracy?: number;
  heading?: number;
  speed?: number;
}

export interface GeofenceConfig {
  latitude: number;
  longitude: number;
  radius_meters: number;
}

export interface GeofenceStatus {
  inside: boolean;
  distance_meters: number;
  accuracy_meters: number;
  last_check: string;
}

export interface UseGeolocationOptions {
  enableHighAccuracy?: boolean;
  timeout?: number;
  maximumAge?: number;
  watchPosition?: boolean; // Continuous tracking
  autoRequestPermission?: boolean;
  geofence?: GeofenceConfig;
  monitorPermission?: boolean; // Poll permission status for changes
  onPermissionChange?: (granted: boolean) => void; // Callback when permission changes
  onLocationServicesChange?: (enabled: boolean) => void; // Callback when location services are toggled
}

type GeolocationWatchError = {
  message?: string;
};

export interface UseGeolocationReturn {
  position: GeolocationCoords | null;
  geofenceStatus: GeofenceStatus | null;
  error: string | null;
  loading: boolean;
  permissionGranted: boolean;
  permissionDenied: boolean; // Explicitly denied (not just not granted)
  locationServicesDisabled: boolean; // Location services turned off at OS level
  isSupported: boolean;
  isMobile: boolean;
  requestPermission: () => Promise<boolean>;
  getCurrentPosition: (options?: { forceFresh?: boolean }) => Promise<GeolocationCoords | null>;
  checkGeofence: (coords?: GeolocationCoords) => GeofenceStatus | null;
  checkPermissionStatus: () => Promise<boolean>; // Manual permission check
  openLocationSettings: () => Promise<void>; // Open device location settings
}

// ==================== HELPER FUNCTIONS ====================

/**
 * Calculate distance between two coordinates using Haversine formula
 */
function calculateDistance(
  lat1: number,
  lon1: number,
  lat2: number,
  lon2: number
): number {
  const R = 6371e3; // Earth's radius in meters
  const φ1 = (lat1 * Math.PI) / 180;
  const φ2 = (lat2 * Math.PI) / 180;
  const Δφ = ((lat2 - lat1) * Math.PI) / 180;
  const Δλ = ((lon2 - lon1) * Math.PI) / 180;

  const a =
    Math.sin(Δφ / 2) * Math.sin(Δφ / 2) +
    Math.cos(φ1) * Math.cos(φ2) * Math.sin(Δλ / 2) * Math.sin(Δλ / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

  return R * c; // Distance in meters
}

function isBrowserGeolocationTimeout(err: GeolocationPositionError | null | undefined): boolean {
  return Boolean(err && err.code === err.TIMEOUT);
}

function isLocationServicesErrorMessage(errorMsg: string): boolean {
  const msg = errorMsg.toLowerCase();
  return msg.includes('location services are not enabled')
    || msg.includes('location services are disabled')
    || msg.includes('location services disabled')
    || msg.includes('location services turned off')
    || msg.includes('gps is disabled')
    || msg.includes('location is disabled');
}

function isNativeLocationWatchError(error: GeolocationWatchError | null | undefined): boolean {
  const errorMessage = typeof error?.message === 'string' ? error.message.toLowerCase() : '';
  // Match explicit "services off" phrases only — transient errors like
  // "location services temporarily unavailable" must not flip the shared
  // services-disabled flag.
  return Boolean(errorMessage) && (
    errorMessage.includes('location') && (
      errorMessage.includes('disabled')
      || errorMessage.includes('turned off')
      || errorMessage.includes('not enabled')
    )
  );
}

function isNativePermissionWatchError(error: GeolocationWatchError | null | undefined): boolean {
  const errorMessage = typeof error?.message === 'string' ? error.message.toLowerCase() : '';
  return Boolean(errorMessage) && (
    errorMessage.includes('permission')
    || errorMessage.includes('denied')
    || errorMessage.includes('revoked')
  );
}

function mapNativeGeoPositionToCoords(position: Position): GeolocationCoords {
  const coords = position?.coords;
  if (!coords) {
    return {
      latitude: 0,
      longitude: 0,
      accuracy: 0,
      timestamp: position?.timestamp || Date.now(),
    };
  }
  return {
    latitude: coords.latitude,
    longitude: coords.longitude,
    accuracy: coords.accuracy,
    timestamp: position.timestamp || Date.now(),
    altitude: coords.altitude ?? undefined,
    altitudeAccuracy: coords.altitudeAccuracy ?? undefined,
    heading: coords.heading ?? undefined,
    speed: coords.speed ?? undefined,
  };
}

function mapBrowserGeoPositionToCoords(position: GeolocationPosition): GeolocationCoords {
  return {
    latitude: position.coords.latitude,
    longitude: position.coords.longitude,
    accuracy: position.coords.accuracy,
    timestamp: position.timestamp || Date.now(),
    altitude: position.coords.altitude ?? undefined,
    altitudeAccuracy: position.coords.altitudeAccuracy ?? undefined,
    heading: position.coords.heading ?? undefined,
    speed: position.coords.speed ?? undefined,
  };
}

function updateSharedGeolocationSnapshot(coords: GeolocationCoords | null): GeolocationCoords | null {
  // Guard against downgrade writes: producers like the shared service-status
  // watcher emit low-accuracy fixes purely to prove location services are on.
  // Letting a much coarser fix replace a recent, more accurate one poisons
  // every consumer that seeds state from this snapshot (a coarse fix can
  // produce a false "outside geofence" verdict in SmartClock/SmartTask).
  if (coords && sharedGeolocationSnapshot) {
    const existingAgeMs = Date.now() - sharedGeolocationSnapshot.timestamp;
    const isSignificantDowngrade = Number.isFinite(coords.accuracy)
      && Number.isFinite(sharedGeolocationSnapshot.accuracy)
      && coords.accuracy > sharedGeolocationSnapshot.accuracy * 1.5 + 25;
    if (existingAgeMs <= 30_000 && isSignificantDowngrade) {
      // Still counts as a successful fix for permission/services flags —
      // just don't hand the worse coordinates to consumers.
      setSharedGeolocationStatus({
        permissionGranted: true,
        permissionDenied: false,
        locationServicesDisabled: false,
      });
      return sharedGeolocationSnapshot;
    }
  }

  sharedGeolocationSnapshot = coords;
  // A successful position fix is the most authoritative signal we have:
  // the OS just handed us coordinates, which means location services are on
  // and permission is granted. Clear any stale "disabled / not granted"
  // flags from the shared status so hero-action gates (BeginAction,
  // SmartClock) unlock immediately without waiting for the 5s permission
  // poller or for `navigator.permissions` to fire `change`.
  if (coords) {
    setSharedGeolocationStatus({
      permissionGranted: true,
      permissionDenied: false,
      locationServicesDisabled: false,
    });
  }
  return sharedGeolocationSnapshot;
}

function getNativeSettingsModule() {
  if (!nativeSettingsModulePromise) {
    nativeSettingsModulePromise = import('capacitor-native-settings');
  }

  return nativeSettingsModulePromise;
}

export function getSharedGeolocationSnapshot(maxAgeMs = SHARED_GEOLOCATION_FRESH_MS): GeolocationCoords | null {
  if (!sharedGeolocationSnapshot) {
    return null;
  }

  return (Date.now() - sharedGeolocationSnapshot.timestamp) <= maxAgeMs
    ? sharedGeolocationSnapshot
    : null;
}

async function getBrowserGeolocationPosition(options: {
  enableHighAccuracy: boolean;
  timeout: number;
  maximumAge: number;
}): Promise<GeolocationCoords | null> {
  if (typeof navigator === 'undefined' || !navigator.geolocation) {
    return null;
  }

  return new Promise<GeolocationCoords | null>((resolve) => {
    navigator.geolocation.getCurrentPosition(
      (position) => resolve(updateSharedGeolocationSnapshot(mapBrowserGeoPositionToCoords(position))),
      () => resolve(getSharedGeolocationSnapshot(Math.max(options.maximumAge, SHARED_GEOLOCATION_FRESH_MS))),
      options,
    );
  });
}

export async function prewarmSharedGeolocation(options: {
  enableHighAccuracy?: boolean;
  timeout?: number;
  maximumAge?: number;
  forceFresh?: boolean;
} = {}): Promise<GeolocationCoords | null> {
  const enableHighAccuracy = options.enableHighAccuracy ?? true;
  const timeout = options.timeout ?? 8000;
  const maximumAge = options.maximumAge ?? SHARED_GEOLOCATION_FRESH_MS;
  const forceFresh = options.forceFresh ?? false;

  if (!forceFresh) {
    const cachedPosition = getSharedGeolocationSnapshot(Math.max(maximumAge, SHARED_GEOLOCATION_FRESH_MS));
    if (cachedPosition) {
      return cachedPosition;
    }
  }

  if (sharedGeolocationWarmPromise) {
    return sharedGeolocationWarmPromise;
  }

  sharedGeolocationWarmPromise = (async () => {
    const isMobile = Capacitor.isNativePlatform();
    const isNativeSupported = isMobile && Capacitor.isPluginAvailable('Geolocation');
    const hasBrowserGeolocation = typeof window !== 'undefined' && typeof navigator !== 'undefined' && 'geolocation' in navigator;

    if (isNativeSupported) {
      try {
        const permission = await Geolocation.checkPermissions();

        // Propagate fresh OS permission state into the shared snapshot so
        // hero-action gates (SmartTask BeginAction, SmartClock) and the
        // location monitor see the latest truth without waiting for the
        // periodic poller.
        const { granted } = applySharedPermissionCheckResult({
          granted: permission.location === 'granted' || permission.coarseLocation === 'granted',
          denied: permission.location === 'denied' && permission.coarseLocation !== 'granted',
          notHeld: permission.location === 'denied' || permission.location === 'prompt-with-rationale',
        });

        if (!granted) {
          return getSharedGeolocationSnapshot(Math.max(maximumAge, SHARED_GEOLOCATION_FRESH_MS));
        }

        try {
          const warmPosition = await withPromiseTimeout(
            Geolocation.getCurrentPosition({
              enableHighAccuracy: false,
              timeout: 2500,
              maximumAge: Math.max(maximumAge, SHARED_GEOLOCATION_FRESH_MS),
            }),
            4500,
            'Timed out waiting for warm native geolocation response',
          );

          const warmCoords = updateSharedGeolocationSnapshot(mapNativeGeoPositionToCoords(warmPosition));
          if (warmCoords && enableHighAccuracy && warmCoords.accuracy > 120) {
            void Geolocation.getCurrentPosition({
              enableHighAccuracy: true,
              timeout: Math.min(Math.max(timeout, 6000), 15000),
              maximumAge: 0,
            })
              .then((position) => updateSharedGeolocationSnapshot(mapNativeGeoPositionToCoords(position)))
              .catch(() => undefined);
          }

          return warmCoords;
        } catch {
          const livePosition = await withPromiseTimeout(
            Geolocation.getCurrentPosition({
              enableHighAccuracy,
              timeout: Math.min(Math.max(timeout, 4000), 8000),
              maximumAge: 0,
            }),
            Math.min(Math.max(timeout, 4000), 8000) + 2000,
            'Timed out waiting for live native geolocation response',
          );

          return updateSharedGeolocationSnapshot(mapNativeGeoPositionToCoords(livePosition));
        }
      } catch (err: unknown) {
        if (isLocationServicesErrorMessage(getErrorMessage(err, ''))) {
          // Surface the OS-level "location services off" state immediately
          // so the location monitor can fire its disabled callback without
          // waiting for the next polling cycle.
          setSharedGeolocationStatus({ locationServicesDisabled: true });
          return null;
        }
      }
    }

    if (hasBrowserGeolocation) {
      try {
        if (navigator.permissions?.query) {
          const permission = await navigator.permissions.query({ name: 'geolocation' as PermissionName });
          if (permission.state !== 'granted') {
            return getSharedGeolocationSnapshot(Math.max(maximumAge, SHARED_GEOLOCATION_FRESH_MS));
          }
        }
      } catch {
        // Fall through — some browsers do not support permissions query here.
      }

      return getBrowserGeolocationPosition({
        enableHighAccuracy,
        timeout: Math.min(Math.max(timeout, 2500), 6000),
        maximumAge: Math.max(maximumAge, SHARED_GEOLOCATION_FRESH_MS),
      });
    }

    return getSharedGeolocationSnapshot(Math.max(maximumAge, SHARED_GEOLOCATION_FRESH_MS));
  })();

  try {
    return await sharedGeolocationWarmPromise;
  } finally {
    sharedGeolocationWarmPromise = null;
  }
}

/**
 * Fast, authoritative tap-time probe.
 *
 * Used by hero-action gates (BeginAction, SmartClock, SmartActionButton)
 * to resolve the *current* OS truth at the moment the user taps — without
 * waiting for the periodic 3s shared poll or the native watcher cadence.
 *
 * Behaviour:
 *  - If a fresh shared snapshot exists (default <=10s) AND shared status
 *    says permission granted + services on, returns immediately.
 *  - Otherwise issues `Geolocation.getCurrentPosition` with a hard timeout
 *    (default 1500ms) to authoritatively determine state. The
 *    "location services disabled" error is mapped onto the shared store so
 *    every consumer reflects the disabled state immediately.
 *  - Falls back to `navigator.permissions` on web.
 *
 * This function NEVER throws — callers can rely on the returned booleans.
 */
export async function probeLocationServicesNow(options: {
  timeoutMs?: number;
  freshSnapshotMaxAgeMs?: number;
} = {}): Promise<{
  granted: boolean;
  servicesEnabled: boolean;
  position: GeolocationCoords | null;
}> {
  const timeoutMs = options.timeoutMs ?? 1500;
  const freshSnapshotMaxAgeMs = options.freshSnapshotMaxAgeMs ?? 10_000;

  const status = getGeolocationStatusSnapshot();
  const cached = getSharedGeolocationSnapshot(freshSnapshotMaxAgeMs);
  if (cached && status.permissionGranted && !status.locationServicesDisabled && !status.permissionDenied) {
    return { granted: true, servicesEnabled: true, position: cached };
  }

  const isMobile = typeof Capacitor !== 'undefined' && Capacitor.isNativePlatform();
  const isNativeSupported = isMobile && Capacitor.isPluginAvailable('Geolocation');

  if (isNativeSupported) {
    try {
      const pos = await withPromiseTimeout(
        Geolocation.getCurrentPosition({
          enableHighAccuracy: false,
          timeout: timeoutMs,
          // Allow a generous cached fix here — we only care about the OS
          // refusing to answer (services off / permission denied), not about
          // a high-accuracy reading.
          maximumAge: 60_000,
        }),
        timeoutMs + 500,
        'probeLocationServicesNow timeout',
      );
      const coords = mapNativeGeoPositionToCoords(pos);
      updateSharedGeolocationSnapshot(coords); // also clears disabled/denied flags
      return { granted: true, servicesEnabled: true, position: coords };
    } catch (err: unknown) {
      const msg = getErrorMessage(err, '');
      if (isLocationServicesErrorMessage(msg)) {
        // Services off is not a permission revocation — flag only the
        // services state in the shared store.
        setSharedGeolocationStatus({
          locationServicesDisabled: true,
        });
        return { granted: false, servicesEnabled: false, position: null };
      }

      // Could be permission denied or transient. Fall back to a fast
      // permissions check to update the shared store.
      try {
        const perm = await withPromiseTimeout(
          Geolocation.checkPermissions(),
          1000,
          'probeLocationServicesNow checkPermissions timeout',
        );
        const { granted } = applySharedPermissionCheckResult({
          granted: perm.location === 'granted' || perm.coarseLocation === 'granted',
          denied: perm.location === 'denied' && perm.coarseLocation !== 'granted',
          notHeld: perm.location === 'denied' || perm.location === 'prompt-with-rationale',
        });
        return {
          granted,
          servicesEnabled: !geolocationStatusState.locationServicesDisabled,
          position: getSharedGeolocationSnapshot(),
        };
      } catch {
        return {
          granted: geolocationStatusState.permissionGranted,
          servicesEnabled: !geolocationStatusState.locationServicesDisabled,
          position: getSharedGeolocationSnapshot(),
        };
      }
    }
  }

  // Web/browser path
  if (typeof navigator !== 'undefined' && navigator.permissions?.query) {
    try {
      const perm = await navigator.permissions.query({ name: 'geolocation' as PermissionName });
      const granted = perm.state === 'granted';
      const denied = perm.state === 'denied';
      setSharedGeolocationStatus({
        permissionGranted: granted,
        permissionDenied: denied,
        locationServicesDisabled: false,
      });
      return { granted, servicesEnabled: true, position: getSharedGeolocationSnapshot() };
    } catch {
      // fallthrough
    }
  }

  return {
    granted: geolocationStatusState.permissionGranted,
    servicesEnabled: !geolocationStatusState.locationServicesDisabled,
    position: getSharedGeolocationSnapshot(),
  };
}

// ============================================================================
// Burst watch — kept on a single shared subscription so multiple hero buttons
// (SmartTask + SmartClock prewarm) don't open competing native watchers.
// ============================================================================
let sharedBurstWatchActive = false;
let sharedBurstWatchExpiresAt = 0;

const DEFAULT_BURST_DURATION_MS = 30_000;
const DEFAULT_BURST_ACCURATE_THRESHOLD_M = 50;
const DEFAULT_BURST_ACCURATE_FIXES_NEEDED = 2;

/**
 * Open a short-lived watchPosition stream so that by the time the user taps
 * a hero action (SmartTask BeginAction, SmartClock) we already have a fresh,
 * accurate coordinate cached in the shared snapshot. The watcher auto-stops
 * once we have collected enough accurate fixes or once the duration window
 * elapses, whichever comes first.
 *
 * Subsequent calls while a burst is in flight extend the expiry rather than
 * starting a second native watcher.
 */
export function startSharedGeolocationBurstWatch(options: {
  durationMs?: number;
  accurateThresholdM?: number;
  accurateFixesNeeded?: number;
} = {}): void {
  if (typeof window === 'undefined') {
    return;
  }

  const durationMs = options.durationMs ?? DEFAULT_BURST_DURATION_MS;
  const accurateThresholdM = options.accurateThresholdM ?? DEFAULT_BURST_ACCURATE_THRESHOLD_M;
  const accurateFixesNeeded = options.accurateFixesNeeded ?? DEFAULT_BURST_ACCURATE_FIXES_NEEDED;
  const newExpiresAt = Date.now() + durationMs;

  if (sharedBurstWatchActive) {
    if (newExpiresAt > sharedBurstWatchExpiresAt) {
      sharedBurstWatchExpiresAt = newExpiresAt;
    }
    return;
  }

  sharedBurstWatchActive = true;
  sharedBurstWatchExpiresAt = newExpiresAt;

  const isMobile = Capacitor.isNativePlatform();
  const isNativeSupported = isMobile && Capacitor.isPluginAvailable('Geolocation');

  let accurateFixCount = 0;
  let stopped = false;
  let stopFn: (() => void) | null = null;
  let timeoutHandle: ReturnType<typeof setTimeout> | null = null;

  const stop = () => {
    if (stopped) {
      return;
    }
    stopped = true;
    sharedBurstWatchActive = false;
    sharedBurstWatchExpiresAt = 0;
    if (timeoutHandle !== null) {
      clearTimeout(timeoutHandle);
      timeoutHandle = null;
    }
    if (stopFn) {
      try {
        stopFn();
      } catch {
        // ignore
      }
      stopFn = null;
    }
  };

  const handleCoords = (coords: GeolocationCoords | null) => {
    if (!coords || stopped) {
      return;
    }
    updateSharedGeolocationSnapshot(coords);
    if (typeof coords.accuracy === 'number' && coords.accuracy <= accurateThresholdM) {
      accurateFixCount += 1;
      if (accurateFixCount >= accurateFixesNeeded) {
        stop();
        return;
      }
    }
    if (Date.now() >= sharedBurstWatchExpiresAt) {
      stop();
    }
  };

  if (isNativeSupported) {
    let watchId: string | null = null;
    Geolocation.watchPosition(
      { enableHighAccuracy: true, timeout: durationMs, maximumAge: 0 },
      (position, err) => {
        if (err) {
          if (isLocationServicesErrorMessage(getErrorMessage(err, ''))) {
            setSharedGeolocationStatus({ locationServicesDisabled: true });
            stop();
          }
          return;
        }
        if (position) {
          handleCoords(mapNativeGeoPositionToCoords(position));
        }
      },
    )
      .then((id) => {
        if (stopped) {
          try { void Geolocation.clearWatch({ id }); } catch { /* ignore */ }
          return;
        }
        watchId = id;
      })
      .catch(() => {
        // Fallthrough — burst remains "armed" but we couldn't open the watcher.
        stop();
      });

    stopFn = () => {
      if (watchId) {
        try { void Geolocation.clearWatch({ id: watchId }); } catch { /* ignore */ }
      }
    };
  } else if (typeof navigator !== 'undefined' && navigator.geolocation) {
    const watchId = navigator.geolocation.watchPosition(
      (position) => handleCoords(mapBrowserGeoPositionToCoords(position)),
      () => undefined,
      { enableHighAccuracy: true, maximumAge: 0, timeout: durationMs },
    );
    stopFn = () => {
      try { navigator.geolocation.clearWatch(watchId); } catch { /* ignore */ }
    };
  } else {
    stop();
    return;
  }

  timeoutHandle = setTimeout(stop, durationMs);
}

export async function ensureLocationReadyForHeroAction(options: {
  isMobilePlatform: boolean;
  locationServicesDisabled: boolean;
  permissionGranted: boolean;
  permissionDenied: boolean;
  hasPosition: boolean;
  requestPermission: () => Promise<boolean>;
  getCurrentPosition: (options?: { forceFresh?: boolean }) => Promise<GeolocationCoords | null>;
  openLocationSettings: () => Promise<void>;
}): Promise<{
  ready: boolean;
  openedSettings: boolean;
  position: GeolocationCoords | null;
}> {
  const cachedPosition = getSharedGeolocationSnapshot();

  // Fast path: shared/cached state already says we are good — proceed and
  // refresh position lazily in the background.
  if (options.permissionGranted && !options.locationServicesDisabled && !options.permissionDenied) {
    if (!options.hasPosition) {
      void options.getCurrentPosition().catch(() => null);
    }
    return { ready: true, openedSettings: false, position: cachedPosition };
  }

  // Authoritative probe: ask the hook for a current position. Internally that
  // performs a fresh native `Geolocation.checkPermissions()` (and falls back
  // to `requestPermissions()`), so it reflects the OS truth even when our
  // shared React state or per-instance refs are stale during the cold-start
  // window. If we get coords back, permission and services are both fine —
  // regardless of what the cached booleans claim. This prevents the
  // "Location access required" notify when the OS has already granted the
  // permission but our React closure hasn't caught up.
  try {
    const probe = await options.getCurrentPosition();
    if (probe) {
      return { ready: true, openedSettings: false, position: probe };
    }
  } catch {
    // Probe failed unexpectedly — fall through to explicit permission/settings handling.
  }

  // Probe returned no position. Re-read the shared snapshot, since
  // getCurrentPosition() will have synchronously updated the shared store
  // via its internal checkPermissionStatus()/requestPermission() flow.
  const refreshedStatus = getGeolocationStatusSnapshot();

  // Location services off → the system location settings page is the right
  // place to re-enable them.
  if (refreshedStatus.locationServicesDisabled || options.locationServicesDisabled) {
    await options.openLocationSettings();
    return { ready: false, openedSettings: true, position: cachedPosition };
  }

  // Permission denied → the location-services page has no permission
  // toggle; only the app settings page can fix this on native.
  if (refreshedStatus.permissionDenied || options.permissionDenied) {
    if (options.isMobilePlatform) {
      await openNativeAppSettings();
      return { ready: false, openedSettings: true, position: cachedPosition };
    }
    await options.openLocationSettings();
    return { ready: false, openedSettings: true, position: cachedPosition };
  }

  // Permission state is still indeterminate (e.g. "prompt"). Ask explicitly.
  const granted = await options.requestPermission();
  if (!granted) {
    // The OS request failed or was denied — on native the only remaining
    // fix is the app settings page; never leave the user at a warning.
    if (options.isMobilePlatform) {
      await openNativeAppSettings();
      return {
        ready: false,
        openedSettings: true,
        position: getSharedGeolocationSnapshot(),
      };
    }
    return {
      ready: false,
      openedSettings: false,
      position: getSharedGeolocationSnapshot(),
    };
  }

  if (!options.hasPosition) {
    void options.getCurrentPosition().catch(() => null);
  }

  return {
    ready: true,
    openedSettings: false,
    position: getSharedGeolocationSnapshot(),
  };
}

function withPromiseTimeout<T>(promise: Promise<T>, timeoutMs: number, timeoutMessage: string): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const timeoutId = window.setTimeout(() => {
      reject(new Error(timeoutMessage));
    }, timeoutMs);

    promise
      .then((value) => {
        window.clearTimeout(timeoutId);
        resolve(value);
      })
      .catch((error) => {
        window.clearTimeout(timeoutId);
        reject(error);
      });
  });
}

// ==================== HOOK ====================

export function useGeolocation(options: UseGeolocationOptions = {}): UseGeolocationReturn {
  const {
    enableHighAccuracy = true,
    timeout = 30000, // Increased from 10s to 30s for GPS cold start
    maximumAge = 5000, // Allow cached position up to 5 seconds old
    watchPosition = false,
    autoRequestPermission = true,
    geofence,
    monitorPermission = true, // Enable by default for SmartClock
    onPermissionChange,
    onLocationServicesChange,
  } = options;

  const initialSharedPosition = getSharedGeolocationSnapshot(Math.max(maximumAge, SHARED_GEOLOCATION_FRESH_MS));
  const initialStatus = getGeolocationStatusSnapshot();
  const [position, setPosition] = useState<GeolocationCoords | null>(initialSharedPosition);
  const [geofenceStatus, setGeofenceStatus] = useState<GeofenceStatus | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  // Local state mirrors the shared geolocation status store so every consumer
  // (SmartClock, SmartTaskShell, BeginActionModal, ...) sees the same booleans
  // and we don't pay for N parallel permission checks on the native bridge.
  const [permissionGranted, setLocalPermissionGranted] = useState<boolean>(initialStatus.permissionGranted);
  const [permissionDenied, setLocalPermissionDenied] = useState<boolean>(initialStatus.permissionDenied);
  const [locationServicesDisabled, setLocalLocationServicesDisabled] = useState<boolean>(initialStatus.locationServicesDisabled);

  const setPermissionGranted = useCallback((value: boolean | ((current: boolean) => boolean)) => {
    const resolved = typeof value === 'function'
      ? (value as (c: boolean) => boolean)(geolocationStatusState.permissionGranted)
      : value;
    setSharedGeolocationStatus({ permissionGranted: resolved });
  }, []);
  const setPermissionDenied = useCallback((value: boolean | ((current: boolean) => boolean)) => {
    const resolved = typeof value === 'function'
      ? (value as (c: boolean) => boolean)(geolocationStatusState.permissionDenied)
      : value;
    setSharedGeolocationStatus({ permissionDenied: resolved });
  }, []);
  const setLocationServicesDisabled = useCallback((value: boolean | ((current: boolean) => boolean)) => {
    const resolved = typeof value === 'function'
      ? (value as (c: boolean) => boolean)(geolocationStatusState.locationServicesDisabled)
      : value;
    setSharedGeolocationStatus({ locationServicesDisabled: resolved });
  }, []);

  const watchId = useRef<string | number | null>(null);
  const resumeRefreshTimeoutsRef = useRef<number[]>([]);
  const resumeRefreshDeferredRef = useRef(false);
  const lastPermissionState = useRef<boolean | null>(null);
  const lastLocationServicesState = useRef<boolean | null>(null);
  const locationServicesDisabledRef = useRef<boolean>(false);
  const pendingPositionRefresh = useRef<boolean>(false); // Flag to trigger position refresh
  const positionRef = useRef<GeolocationCoords | null>(initialSharedPosition); // Stable ref to avoid callback cascade
  const currentPositionRequestRef = useRef<Promise<GeolocationCoords | null> | null>(null);
  const permissionRequestRef = useRef<Promise<boolean> | null>(null);
  const backgroundRefreshInFlightRef = useRef<boolean>(false);
  const geofenceRef = useRef(geofence); // Stable ref for geofence config
  geofenceRef.current = geofence;
  const isMobile = Capacitor.isNativePlatform();
  const isNativeSupported = isMobile && Capacitor.isPluginAvailable('Geolocation');
  const hasBrowserGeolocation = typeof window !== 'undefined' && typeof navigator !== 'undefined' && 'geolocation' in navigator;
  const isSupported = isNativeSupported || hasBrowserGeolocation;

  const clearPosition = useCallback(() => {
    positionRef.current = null;
    updateSharedGeolocationSnapshot(null);
    setPosition(null);
    setGeofenceStatus(null);
  }, []);

  const handleBrowserPermissionRevoked = useCallback((message?: string) => {
    lastPermissionState.current = false;
    lastLocationServicesState.current = true;
    locationServicesDisabledRef.current = false;
    setPermissionGranted(false);
    setPermissionDenied(true);
    setLocationServicesDisabled(false);
    clearPosition();
    setError(message || 'Location permission denied');
  }, [clearPosition, setLocationServicesDisabled, setPermissionDenied, setPermissionGranted]);

  /**
   * Check current permission status (can be called manually)
   * Also detects if location services are disabled at OS level
   */
  const checkPermissionStatus = useCallback(async (): Promise<boolean> => {
    if (!isSupported) return false;

    if (!isMobile) {
      try {
        if (navigator.permissions?.query) {
          const permission = await navigator.permissions.query({ name: 'geolocation' as PermissionName });
          const granted = permission.state === 'granted';
          const denied = permission.state === 'denied';

          if (lastPermissionState.current !== null && lastPermissionState.current !== granted) {
            debugLog('[Geolocation]', `Browser permission changed: ${lastPermissionState.current} -> ${granted}`);
            onPermissionChange?.(granted);
          }

          lastPermissionState.current = granted;
          setPermissionGranted(granted);
          setPermissionDenied(denied);
          setLocationServicesDisabled(false);
          locationServicesDisabledRef.current = false;
          lastLocationServicesState.current = true;
          if (denied) {
            clearPosition();
          }
          return granted;
        }
      } catch {
        debugLog('[Geolocation]', 'Browser Permissions API unavailable, falling back to cached state');
      }

      const hasKnownPosition = positionRef.current !== null;
      setPermissionGranted(hasKnownPosition);
      setPermissionDenied(false);
      setLocationServicesDisabled(false);
      locationServicesDisabledRef.current = false;
      lastPermissionState.current = hasKnownPosition;
      lastLocationServicesState.current = true;
      return hasKnownPosition;
    }

    try {
      const permission: PermissionStatus = await Geolocation.checkPermissions();
      // checkPermissions cannot see the OS location-services toggle, and a
      // fresh position fix is more authoritative than a transient 'prompt'
      // result — applySharedPermissionCheckResult guards both cases.
      const { granted, denied } = applySharedPermissionCheckResult({
        granted: permission.location === 'granted' || permission.coarseLocation === 'granted',
        // Only mark as denied if BOTH fine and coarse location are denied
        // On Android 12+, user can grant approximate-only: location='denied', coarseLocation='granted'
        denied: permission.location === 'denied' && permission.coarseLocation !== 'granted',
        notHeld: permission.location === 'denied' || permission.location === 'prompt-with-rationale',
      });

      // Only log on transitions — the shared 5s poll otherwise spams the console.
      if (lastPermissionState.current !== granted) {
        debugLog('[Geolocation]', `Permission status: location=${permission.location} coarse=${permission.coarseLocation} granted=${granted} denied=${denied}`);
      }

      // Detect permission change
      if (lastPermissionState.current !== null && lastPermissionState.current !== granted) {
        debugLog('[Geolocation]', `Permission changed: ${lastPermissionState.current} -> ${granted}`);
        onPermissionChange?.(granted);
      }

      lastPermissionState.current = granted;
      setPermissionGranted(granted);
      setPermissionDenied(denied);

      // A permission grant does NOT imply location services are on — only
      // a successful position fix proves that (the shared snapshot clears
      // the flag itself). Run the "services came back" bookkeeping only
      // once the shared flag has actually cleared.
      if (granted && !geolocationStatusState.locationServicesDisabled) {
        if (lastLocationServicesState.current === false) {
          // Location services were disabled and are now enabled
          debugLog('[Geolocation]', 'Location services re-enabled - will refresh position');
          pendingPositionRefresh.current = true; // Signal to refresh position
          onLocationServicesChange?.(true);
        }
        lastLocationServicesState.current = true;
        locationServicesDisabledRef.current = false;
      }

      return granted;
    } catch (err: unknown) {
      // Check if error is specifically about location services being disabled
      const errorMsg = getErrorMessage(err, '');
      const isLocationServicesError = isLocationServicesErrorMessage(errorMsg);

      if (isLocationServicesError) {
        // Log as info, not error - this is an expected state
        debugLog('[Geolocation]', 'Location services are disabled at OS level');

        // Detect location services state change
        if (lastLocationServicesState.current !== false) {
          debugLog('[Geolocation]', 'Location services disabled - firing callback');
          onLocationServicesChange?.(false);
        }

        lastLocationServicesState.current = false;
        setLocationServicesDisabled(true);
        locationServicesDisabledRef.current = true;
        // Don't log as error for this expected case
        return false;
      }

      // Unexpected error — try position-based fallback to detect actual permission state
      // Some Android devices throw unexpected errors from checkPermissions but still allow position access
      console.warn('[Geolocation] Permission check failed, trying position fallback:', errorMsg);
      try {
        const pos = await Geolocation.getCurrentPosition({ enableHighAccuracy: false, timeout: 5000, maximumAge: 60000 });
        if (pos) {
          debugLog('[Geolocation]', 'Position fallback succeeded — permission IS granted despite checkPermissions error');
          setPermissionGranted(true);
          setPermissionDenied(false);
          setLocationServicesDisabled(false);
          locationServicesDisabledRef.current = false;
          lastPermissionState.current = true;
          lastLocationServicesState.current = true;
          // Also update position since we have it
          const coords = mapNativeGeoPositionToCoords(pos);
          positionRef.current = coords;
          updateSharedGeolocationSnapshot(coords);
          setPosition(coords);
          return true;
        }
      } catch (posErr: unknown) {
        console.warn('[Geolocation] Position fallback also failed:', getErrorMessage(posErr, String(posErr)));
      }

      return false;
    }
  }, [clearPosition, isSupported, isMobile, onPermissionChange, onLocationServicesChange, setLocationServicesDisabled, setPermissionDenied, setPermissionGranted]);

  // Track whether we've auto-requested permission (do it only once)
  const hasAutoRequested = useRef(false);
  // Ref to requestPermission so the mount effect can call it without depending on it
  const requestPermissionRef = useRef<() => Promise<boolean>>(async () => false);

  // Subscribe local React state to the shared geolocation status store so
  // every consumer of useGeolocation sees the same booleans without each one
  // hitting the native bridge to find out for itself.
  useEffect(() => {
    const unsubscribe = subscribeToGeolocationStatus((next) => {
      setLocalPermissionGranted(next.permissionGranted);
      setLocalPermissionDenied(next.permissionDenied);
      setLocalLocationServicesDisabled(next.locationServicesDisabled);
      // Keep the instance ref in sync — resume/visibility handlers gate on
      // locationServicesDisabledRef and must reflect the shared truth even
      // when another consumer wrote it.
      locationServicesDisabledRef.current = next.locationServicesDisabled;
    });
    return unsubscribe;
  }, []);

  // Register per-instance permission/services-change callbacks against the
  // shared store. The shared store fires them only on actual transitions.
  useEffect(() => {
    if (!onPermissionChange) return;
    geolocationPermissionChangeCallbacks.add(onPermissionChange);
    return () => { geolocationPermissionChangeCallbacks.delete(onPermissionChange); };
  }, [onPermissionChange]);

  useEffect(() => {
    if (!onLocationServicesChange) return;
    geolocationServicesChangeCallbacks.add(onLocationServicesChange);
    return () => { geolocationServicesChangeCallbacks.delete(onLocationServicesChange); };
  }, [onLocationServicesChange]);

  // Check permission status on mount and start (shared) monitoring
  useEffect(() => {
    if (!isSupported) return;

    let releasePermissionMonitor: (() => void) | null = null;

    // Initial check + auto-request if in 'prompt' state
    const initPermissions = async () => {
      const granted = await checkPermissionStatus();

      // If not granted and not explicitly denied, auto-request once
      // This handles: "Only this time" expired, first launch, or device quirks
      if (
        autoRequestPermission
        && !granted
        && !hasAutoRequested.current
        && lastLocationServicesState.current !== false
        && !locationServicesDisabledRef.current
      ) {
        hasAutoRequested.current = true;
        try {
          const perm = await Geolocation.checkPermissions().catch(() => null);
          const isDenied = perm && perm.location === 'denied' && perm.coarseLocation !== 'granted';
          if (!isDenied) {
            debugLog('[Geolocation]', 'Auto-requesting permission (status not granted, not denied)');
            const result = await requestPermissionRef.current();
            debugLog('[Geolocation]', 'Auto-request result:', result);
          }
        } catch (e) {
          console.warn('[Geolocation] Auto-request failed:', e);
        }
      }
    };
    initPermissions();

    if (monitorPermission) {
      // One shared 5s permission poll for the whole app, regardless of how
      // many useGeolocation instances are mounted.
      releasePermissionMonitor = registerSharedPermissionMonitor();
    }

    return () => {
      if (releasePermissionMonitor) {
        releasePermissionMonitor();
      }
    };
  }, [isSupported, monitorPermission, checkPermissionStatus, autoRequestPermission]);

  /**
   * Check geofence status for given coordinates
   */
  const checkGeofence = useCallback(
    (coords?: GeolocationCoords): GeofenceStatus | null => {
      const fence = geofenceRef.current;
      if (!fence) return null;
      // Use passed coords, or fall back to latest position ref (NOT state — avoids dep cascade)
      const currentCoords = coords || positionRef.current;
      if (!currentCoords) return null;

      const distance = calculateDistance(
        currentCoords.latitude,
        currentCoords.longitude,
        fence.latitude,
        fence.longitude
      );

      const status: GeofenceStatus = {
        inside: distance <= fence.radius_meters,
        distance_meters: Math.round(distance),
        accuracy_meters: Math.round(currentCoords.accuracy),
        last_check: new Date().toISOString(),
      };

      setGeofenceStatus(status);
      return status;
    },
    [] // Stable — uses refs internally
  );

  const applyPosition = useCallback((coords: GeolocationCoords) => {
    positionRef.current = coords;
    updateSharedGeolocationSnapshot(coords);
    setPosition(coords);
    setError(null);

    if (geofenceRef.current) {
      checkGeofence(coords);
    }
  }, [checkGeofence]);

  const mapPositionToCoords = useCallback((position: Position): GeolocationCoords => mapNativeGeoPositionToCoords(position), []);

  const mapBrowserPositionToCoords = useCallback((position: GeolocationPosition): GeolocationCoords => mapBrowserGeoPositionToCoords(position), []);

  const refreshPositionInBackground = useCallback(async (reason: string) => {
    if (!isSupported || !isNativeSupported || backgroundRefreshInFlightRef.current) return;

    backgroundRefreshInFlightRef.current = true;
    try {
      debugLog('[Geolocation]', `Background GPS refinement started (${reason})...`);
      const pos = await Geolocation.getCurrentPosition({
        enableHighAccuracy: true,
        timeout: 15000,
        maximumAge: 0,
      });
      const coords = mapPositionToCoords(pos);
      applyPosition(coords);
      debugLog('[Geolocation]', 'Background GPS refinement complete:', coords);
    } catch (err: unknown) {
      debugLog('[Geolocation]', 'Background GPS refinement skipped:', getErrorMessage(err, String(err)));
    } finally {
      backgroundRefreshInFlightRef.current = false;
    }
  }, [isSupported, isNativeSupported, mapPositionToCoords, applyPosition]);

  /**
   * Request location permission
   */
  const requestPermission = useCallback(async (): Promise<boolean> => {
    if (!isSupported) {
      setError('Geolocation not supported on this platform');
      return false;
    }

    if (permissionGranted && !locationServicesDisabled) {
      return true;
    }

    if (locationServicesDisabledRef.current || lastLocationServicesState.current === false) {
      setLocationServicesDisabled(true);
      setError('Location services disabled');
      return false;
    }

    if (permissionRequestRef.current) {
      return permissionRequestRef.current;
    }

    if (!isMobile) {
      const requestPromise = (async () => {
        try {
          setLoading(true);
          debugLog('[Geolocation]', 'Requesting browser geolocation permission...');

          const granted = await new Promise<boolean>((resolve) => {
            navigator.geolocation.getCurrentPosition(
              (position) => {
                const coords = mapBrowserPositionToCoords(position);
                applyPosition(coords);
                setPermissionGranted(true);
                setPermissionDenied(false);
                setLocationServicesDisabled(false);
                locationServicesDisabledRef.current = false;
                lastPermissionState.current = true;
                lastLocationServicesState.current = true;
                resolve(true);
              },
              (err) => {
                const denied = err.code === err.PERMISSION_DENIED;
                const servicesUnavailable = err.code === err.POSITION_UNAVAILABLE;

                if (denied) {
                  handleBrowserPermissionRevoked(err.message || 'Location permission denied');
                } else {
                  setPermissionGranted(false);
                  setPermissionDenied(false);
                  setLocationServicesDisabled(servicesUnavailable);
                  locationServicesDisabledRef.current = servicesUnavailable;
                  setError(err.message || 'Failed to access location');
                }
                resolve(false);
              },
              {
                enableHighAccuracy,
                timeout,
                maximumAge,
              }
            );
          });

          if (!granted) {
            debugLog('[Geolocation]', 'Browser permission request was not granted');
          }

          return granted;
        } catch (err: unknown) {
          const errorMessage = getErrorMessage(err, 'Failed to request location permission');
          setError(errorMessage);
          console.warn('[Geolocation] Browser permission error:', errorMessage);
          return false;
        } finally {
          setLoading(false);
          permissionRequestRef.current = null;
        }
      })();

      permissionRequestRef.current = requestPromise;
      return requestPromise;
    }

    try {
      const existingPermission = await Geolocation.checkPermissions();
      const alreadyGranted = existingPermission.location === 'granted' || existingPermission.coarseLocation === 'granted';
      if (alreadyGranted) {
        setPermissionGranted(true);
        setPermissionDenied(false);
        lastPermissionState.current = true;
        if (!geolocationStatusState.locationServicesDisabled) {
          locationServicesDisabledRef.current = false;
          lastLocationServicesState.current = true;
        }
        return true;
      }
    } catch {
      // Fall through to the actual permission request if a direct check fails.
    }

    const requestPromise = (async () => {
      try {
        setLoading(true);
        debugLog('[Geolocation]', 'Requesting permission...');

        const permission: PermissionStatus = await requestNativePermissionDeduped();

        const granted =
          permission.location === 'granted' ||
          permission.coarseLocation === 'granted';
        // 'prompt' (e.g. the dialog was dismissed or superseded) is not a
        // denial — only flag denied when the OS explicitly reports it, so
        // hero actions re-prompt instead of routing to app settings.
        const denied = permission.location === 'denied' && permission.coarseLocation !== 'granted';

        setPermissionGranted(granted);
        setPermissionDenied(denied);
        if (granted) {
          lastPermissionState.current = true;
          // A granted permission does not prove services are on — keep the
          // shared services flag until a successful fix clears it.
          if (!geolocationStatusState.locationServicesDisabled) {
            locationServicesDisabledRef.current = false;
            lastLocationServicesState.current = true;
          }
        }

        if (!granted) {
          setError('Location permission denied');
          debugLog('[Geolocation]', 'Permission denied:', permission);
        } else {
          setError(null);
          debugLog('[Geolocation]', 'Permission granted');
        }

        return granted;
      } catch (err: unknown) {
        const errorMsg = getErrorMessage(err, 'Failed to request location permission');

        // Check if this is a location services disabled error
        const isLocationServicesError = isLocationServicesErrorMessage(errorMsg);

        if (isLocationServicesError) {
          // Handle gracefully - not an error, just disabled
          debugLog('[Geolocation]', 'Cannot request permission - location services disabled');
          setLocationServicesDisabled(true);
          locationServicesDisabledRef.current = true;
          lastLocationServicesState.current = false;
          setError('Location services disabled');
        } else {
          setError(errorMsg);
          console.warn('[Geolocation] Permission error:', errorMsg);
        }
        return false;
      } finally {
        setLoading(false);
        permissionRequestRef.current = null;
      }
    })();

    permissionRequestRef.current = requestPromise;
    return requestPromise;
  }, [isSupported, permissionGranted, locationServicesDisabled, isMobile, mapBrowserPositionToCoords, applyPosition, enableHighAccuracy, timeout, maximumAge, handleBrowserPermissionRevoked, setLocationServicesDisabled, setPermissionDenied, setPermissionGranted]);

  // Keep the ref in sync so the mount effect can call it
  requestPermissionRef.current = requestPermission;

  /**
   * Get current position once with fallback strategy
   * First tries high accuracy, falls back to lower accuracy if timeout
   */
  const getCurrentPosition = useCallback(async (options?: { forceFresh?: boolean }): Promise<GeolocationCoords | null> => {
    const forceFresh = options?.forceFresh === true;
    if (!isSupported) {
      setError('Geolocation not supported on this platform');
      return null;
    }

    if (currentPositionRequestRef.current) {
      return currentPositionRequestRef.current;
    }

    if (!permissionGranted) {
      const alreadyGranted = await checkPermissionStatus();
      const granted = alreadyGranted || await requestPermission();
      if (!granted) return null;
    }

    const existingPosition = positionRef.current || getSharedGeolocationSnapshot(Math.max(maximumAge, 60000));
    if (!forceFresh && existingPosition && (Date.now() - existingPosition.timestamp) <= Math.max(maximumAge, 60000)) {
      if (existingPosition.accuracy > 120) {
        void refreshPositionInBackground('existing_position_coarse');
      }
      return existingPosition;
    }

    const tryGetPosition = async (
      highAccuracy: boolean,
      attemptTimeout: number,
      attemptMaximumAge: number
    ): Promise<Position> => {
      return withPromiseTimeout(
        Geolocation.getCurrentPosition({
          enableHighAccuracy: highAccuracy,
          timeout: attemptTimeout,
          maximumAge: attemptMaximumAge,
        }),
        attemptTimeout + 2000,
        'Timed out waiting for native geolocation response'
      );
    };

    const requestPromise = (async (): Promise<GeolocationCoords | null> => {
      setLoading(true);
      setError(null);
      debugLog('[Geolocation]', 'Getting current position...');

      try {
        if (!isMobile) {
          return await new Promise<GeolocationCoords | null>((resolve) => {
            let settled = false;
            const settle = (value: GeolocationCoords | null) => {
              if (settled) return;
              settled = true;
              resolve(value);
            };
            const fallbackTimeoutId = window.setTimeout(() => {
              setError('Timed out waiting for browser geolocation response');
              debugLog('[Geolocation]', 'Browser geolocation hard timeout reached');
              settle(positionRef.current);
            }, timeout + 2000);

            navigator.geolocation.getCurrentPosition(
              (position) => {
                window.clearTimeout(fallbackTimeoutId);
                const coords = mapBrowserPositionToCoords(position);
                applyPosition(coords);
                debugLog('[Geolocation]', 'Browser position acquired:', coords);
                settle(coords);
              },
              (err) => {
                window.clearTimeout(fallbackTimeoutId);
                const denied = err.code === err.PERMISSION_DENIED;
                const servicesUnavailable = err.code === err.POSITION_UNAVAILABLE;

                if (denied) {
                  handleBrowserPermissionRevoked(err.message || 'Location permission denied');
                } else {
                  setPermissionGranted((current) => denied ? false : current);
                  setPermissionDenied(false);
                  setLocationServicesDisabled(servicesUnavailable);
                  setError(err.message || 'Failed to get current position');
                }
                debugLog('[Geolocation]', 'Browser position unavailable:', err.message || err);
                settle(positionRef.current);
              },
              {
                enableHighAccuracy,
                timeout,
                maximumAge: forceFresh ? 0 : maximumAge,
              }
            );
          });
        }

        if (!forceFresh) {
          try {
            // Fast path: ask for any warm/cached fix first. This is usually instant.
            const warmPosition = await tryGetPosition(false, 2500, Math.max(maximumAge, 60000));
            const warmCoords = mapPositionToCoords(warmPosition);
            applyPosition(warmCoords);

            if (enableHighAccuracy && warmCoords.accuracy > 120) {
              void refreshPositionInBackground('warm_fix_coarse');
            }

            debugLog('[Geolocation]', 'Warm position acquired:', warmCoords);
            return warmCoords;
          } catch (warmErr: unknown) {
            debugLog('[Geolocation]', 'No warm position available, requesting live GPS fix...', getErrorMessage(warmErr, String(warmErr)));
          }
        }

        let position: Position;

        try {
          // Live attempt: keep this shorter than before so the UI is not blocked for ages.
          position = await tryGetPosition(enableHighAccuracy, 8000, 0);
        } catch (firstErr: unknown) {
          const firstErrorMessage = getErrorMessage(firstErr, '');
          if (firstErrorMessage.includes('timeout') || firstErrorMessage.includes('Could not obtain location')) {
            debugLog('[Geolocation]', 'High accuracy timed out, trying balanced fallback...');
            position = await tryGetPosition(false, Math.min(timeout, 6000), forceFresh ? 0 : Math.max(maximumAge, 15000));
          } else {
            throw firstErr;
          }
        }

        const coords = mapPositionToCoords(position);
        applyPosition(coords);
        debugLog('[Geolocation]', 'Position acquired:', coords);
        return coords;
      } catch (err: unknown) {
        const errorMsg = getErrorMessage(err, 'Failed to get current position');
        debugLog('[Geolocation]', 'Could not get position:', errorMsg);
        setError(errorMsg);
        return positionRef.current;
      } finally {
        setLoading(false);
        currentPositionRequestRef.current = null;
      }
    })();

    currentPositionRequestRef.current = requestPromise;
    return requestPromise;
  }, [isSupported, permissionGranted, enableHighAccuracy, timeout, maximumAge, requestPermission, mapPositionToCoords, mapBrowserPositionToCoords, applyPosition, refreshPositionInBackground, checkPermissionStatus, isMobile, handleBrowserPermissionRevoked, setLocationServicesDisabled, setPermissionDenied, setPermissionGranted]);

  /**
   * Listen for app resume events to check permissions immediately
   * when user returns from settings (instead of waiting for 5s poll)
   */
  useEffect(() => {
    if (!isSupported || !isMobile) return;

    let isDisposed = false;
    let listener: { remove: () => Promise<void> } | null = null;

    const handleAppStateChange = async (appState: { isActive: boolean }) => {
      if (appState.isActive) {
        if (isBiometricLockActive()) {
          if (!resumeRefreshDeferredRef.current) {
            resumeRefreshDeferredRef.current = true;
            debugLog('[Geolocation]', 'Resume refresh deferred until biometric unlock');
            void waitForBiometricUnlock().then((unlocked) => {
              resumeRefreshDeferredRef.current = false;
              if (!unlocked || isDisposed) {
                return;
              }

              void handleAppStateChange({ isActive: true });
            });
          }
          return;
        }

        debugLog('[Geolocation]', 'App resumed - checking permissions immediately');
        const granted = await checkPermissionStatus();

        if ((pendingPositionRefresh.current || !positionRef.current) && granted && !locationServicesDisabledRef.current) {
          debugLog('[Geolocation]', 'Triggering position refresh after settings return');
          pendingPositionRefresh.current = false;
          try {
            await getCurrentPosition();
          } catch (err) {
            console.warn('[Geolocation] Position refresh failed:', err);
          }
        }
      }
    };

    void (async () => {
      listener = await App.addListener('appStateChange', handleAppStateChange);
      if (isDisposed && listener) {
        await listener.remove();
        listener = null;
      }
    })();

    return () => {
      isDisposed = true;
      if (listener) {
        void listener.remove();
        listener = null;
      }
    };
  }, [isSupported, isMobile, checkPermissionStatus, permissionGranted, locationServicesDisabled, getCurrentPosition]);

  useEffect(() => {
    if (!isSupported || typeof window === 'undefined' || typeof document === 'undefined') {
      return;
    }

    let cancelled = false;

    const clearResumeRefreshTimeouts = () => {
      resumeRefreshTimeoutsRef.current.forEach((timeoutId) => window.clearTimeout(timeoutId));
      resumeRefreshTimeoutsRef.current = [];
    };

    const refreshPermissionState = async (reason: string) => {
      debugLog('[Geolocation]', `Refreshing permission state after ${reason}`);
      const granted = await checkPermissionStatus();
      if (cancelled) {
        return;
      }

      if ((pendingPositionRefresh.current || !positionRef.current) && granted && !locationServicesDisabledRef.current) {
        debugLog('[Geolocation]', `Refreshing position after ${reason}`);
        pendingPositionRefresh.current = false;
        try {
          await getCurrentPosition();
        } catch (err) {
          console.warn('[Geolocation] Position refresh after visibility/focus change failed:', err);
        }
      }
    };

    const scheduleRefreshPermissionState = (reason: string) => {
      if (isBiometricLockActive()) {
        clearResumeRefreshTimeouts();

        if (!resumeRefreshDeferredRef.current) {
          resumeRefreshDeferredRef.current = true;
          debugLog('[Geolocation]', `${reason} refresh deferred until biometric unlock`);
          void waitForBiometricUnlock().then((unlocked) => {
            resumeRefreshDeferredRef.current = false;
            if (!unlocked || cancelled) {
              return;
            }

            scheduleRefreshPermissionState(`${reason}:post-unlock`);
          });
        }

        return;
      }

      clearResumeRefreshTimeouts();
      [0, 350, 1200, 2500].forEach((delayMs) => {
        const timeoutId = window.setTimeout(() => {
          void refreshPermissionState(`${reason}:${delayMs}ms`);
        }, delayMs);
        resumeRefreshTimeoutsRef.current.push(timeoutId);
      });
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        scheduleRefreshPermissionState('visibilitychange');
      }
    };

    const handleFocus = () => {
      scheduleRefreshPermissionState('focus');
    };

    const handlePageShow = () => {
      scheduleRefreshPermissionState('pageshow');
    };

    window.addEventListener('focus', handleFocus);
    window.addEventListener('pageshow', handlePageShow);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      cancelled = true;
      resumeRefreshDeferredRef.current = false;
      clearResumeRefreshTimeouts();
      window.removeEventListener('focus', handleFocus);
      window.removeEventListener('pageshow', handlePageShow);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [checkPermissionStatus, getCurrentPosition, isSupported]);

  /**
   * Start watching position (continuous tracking)
   */
  const startWatching = useCallback(async () => {
    if (!isSupported) return;
    if (!permissionGranted) {
      const alreadyGranted = await checkPermissionStatus();
      if (!alreadyGranted) {
        const granted = await requestPermission();
        if (!granted) return;
      }
    }
    if (watchId.current) return; // Already watching

    debugLog('[Geolocation]', 'Starting position watch...');

    if (!isMobile) {
      const id = navigator.geolocation.watchPosition(
        (position) => {
          const coords = mapBrowserPositionToCoords(position);
          debugLog('[Geolocation]', 'Browser watch update received:', coords);
          applyPosition(coords);
        },
        (err) => {
          if (isBrowserGeolocationTimeout(err)) {
            console.debug('[Geolocation] Browser watch timed out waiting for a fresh fix');
            return;
          }

          if (err.code === err.PERMISSION_DENIED) {
            debugLog('[Geolocation]', 'Browser watch permission revoked');
            handleBrowserPermissionRevoked(err.message || 'Location permission denied');
            if (watchId.current && typeof watchId.current === 'number') {
              navigator.geolocation.clearWatch(watchId.current);
              watchId.current = null;
            }
            return;
          }

          console.warn('[Geolocation] Browser watch error:', err.message || err);
          setError(err.message || 'Browser watch failed');
        },
        {
          enableHighAccuracy,
          maximumAge,
        }
      );

      watchId.current = id;
      debugLog('[Geolocation]', 'Browser position watch started:', id);
      return;
    }

    try {
      const id = await Geolocation.watchPosition(
        {
          enableHighAccuracy,
          timeout,
          maximumAge,
        },
        (position, err) => {
          if (err) {
            console.error('[Geolocation] Watch error:', err);
            setError(err.message);
            return;
          }

          if (position) {
            const coords = mapPositionToCoords(position);
            debugLog('[Geolocation]', 'Watch update received:', coords);
            applyPosition(coords);
          }
        }
      );

      watchId.current = id;
      debugLog('[Geolocation]', 'Position watch started:', id);
    } catch (err: unknown) {
      const errorMessage = getErrorMessage(err, 'Failed to start watch');
      console.error('[Geolocation] Failed to start watch:', errorMessage);
      setError(errorMessage);
    }
  }, [isSupported, permissionGranted, enableHighAccuracy, timeout, maximumAge, requestPermission, mapPositionToCoords, mapBrowserPositionToCoords, applyPosition, checkPermissionStatus, isMobile, handleBrowserPermissionRevoked]);

  /**
   * Stop watching position
   */
  const stopWatching = useCallback(async () => {
    const id = watchId.current;
    if (!id) return;

    // Null out synchronously to prevent double-clear race condition
    watchId.current = null;
    debugLog('[Geolocation]', 'Stopping position watch:', id);

    try {
      if (typeof id === 'number') {
        navigator.geolocation.clearWatch(id);
        debugLog('[Geolocation]', 'Browser position watch stopped');
        return;
      }

      await Geolocation.clearWatch({ id });
      debugLog('[Geolocation]', 'Position watch stopped');
    } catch (err: unknown) {
      // Silently ignore "WatchId not found" — expected during cleanup races
      const errorMessage = getErrorMessage(err, String(err));
      if (!errorMessage.includes('WatchId')) {
        console.error('[Geolocation] Failed to stop watch:', errorMessage);
      }
    }
  }, []);

  // Register a slot in the SHARED native service-status watcher (one native
  // Geolocation.watchPosition for the whole app, refcounted across consumers).
  // The shared watcher writes to the shared status store so every hook
  // instance picks up changes via the subscription effect above.
  useEffect(() => {
    if (!isMobile || !isNativeSupported || !monitorPermission) {
      return;
    }
    const release = registerSharedNativeServiceStatusWatcher();
    return () => { release(); };
  }, [isMobile, isNativeSupported, monitorPermission]);

  /**
   * Start/stop watching based on options
   * Dependencies intentionally exclude startWatching/stopWatching to prevent
   * the start→position→checkGeofence→startWatching→restart cascade.
   * The latest versions are captured via refs.
   */
  const startWatchingRef = useRef(startWatching);
  startWatchingRef.current = startWatching;
  const stopWatchingRef = useRef(stopWatching);
  stopWatchingRef.current = stopWatching;

  useEffect(() => {
    if (watchPosition && permissionGranted) {
      startWatchingRef.current();
    }

    return () => {
      if (watchId.current) {
        stopWatchingRef.current();
      }
    };
  }, [watchPosition, permissionGranted]);

  /**
   * Update geofence status when position or geofence changes
   */
  useEffect(() => {
    if (position && geofence) {
      checkGeofence(position);
    }
  }, [position, geofence, checkGeofence]);

  /**
   * Auto-refresh position when location services are re-enabled
   * This runs after permission check detects location services came back
   */
  useEffect(() => {
    if (pendingPositionRefresh.current && permissionGranted && !locationServicesDisabled && !loading) {
      pendingPositionRefresh.current = false;
      debugLog('[Geolocation]', 'Auto-refreshing position after location services restored');

      // Small delay to allow GPS to initialize
      const timer = setTimeout(async () => {
        try {
          const pos = await Geolocation.getCurrentPosition({
            enableHighAccuracy: true,
            timeout: 15000,
            maximumAge: 0, // Force fresh position
          });

          const coords = mapPositionToCoords(pos);
          applyPosition(coords);
          debugLog('[Geolocation]', 'Position refreshed after location services restored:', coords);
        } catch (err: unknown) {
          debugLog('[Geolocation]', 'Could not refresh position:', getErrorMessage(err, 'Unknown refresh error'));
          // Don't set error - will retry on next poll
        }
      }, 1500); // 1.5 second delay for GPS to warm up

      return () => clearTimeout(timer);
    }
  }, [permissionGranted, locationServicesDisabled, loading, mapPositionToCoords, applyPosition]);

  /**
   * Open device location settings
   * Uses capacitor-native-settings plugin on mobile
   */
  const openLocationSettings = useCallback(async (): Promise<void> => {
    if (!isMobile) {
      debugLog('[Geolocation]', 'Cannot open device settings on non-mobile platform');
      setError('Please enable location access in your browser settings, then try again.');
      return;
    }

    if (openLocationSettingsPromise) {
      debugLog('[Geolocation]', 'Location settings already opening — reusing in-flight request');
      return openLocationSettingsPromise;
    }

    openLocationSettingsPromise = (async () => {
      try {
        const { NativeSettings, AndroidSettings, IOSSettings } = await getNativeSettingsModule();

        await NativeSettings.open({
          optionAndroid: AndroidSettings.Location,
          optionIOS: IOSSettings.LocationServices
        });

        debugLog('[Geolocation]', 'Opened location settings');
      } catch (err: unknown) {
        console.error('[Geolocation] Failed to open location settings:', getErrorMessage(err, 'Unknown settings error'));

        // Fallback: Try opening app settings instead
        try {
          const { NativeSettings, AndroidSettings, IOSSettings } = await getNativeSettingsModule();
          await NativeSettings.open({
            optionAndroid: AndroidSettings.ApplicationDetails,
            optionIOS: IOSSettings.App
          });
          debugLog('[Geolocation]', 'Opened app settings as fallback');
        } catch (fallbackErr) {
          console.error('[Geolocation] Fallback to app settings also failed:', fallbackErr);
        }
      } finally {
        window.setTimeout(() => {
          openLocationSettingsPromise = null;
        }, 1500);
      }
    })();

    return openLocationSettingsPromise;
  }, [isMobile]);

  return {
    position,
    geofenceStatus,
    error,
    loading,
    permissionGranted,
    permissionDenied,
    locationServicesDisabled,
    isSupported,
    isMobile,
    requestPermission,
    getCurrentPosition,
    checkGeofence,
    checkPermissionStatus,
    openLocationSettings,
  };
}

export default useGeolocation;
